import { useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type VoiceSyncState = {
  segments: string[];
  currentSegmentIndex: number;
  isPlaying: boolean;
  isPaused: boolean;
  isFinished: boolean;
};

const TTS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`;

// Strip technical artifacts for natural TTS — voice never reads illustration code
const preprocessForSpeech = (text: string): string => {
  let c = text;

  // 1) Remove KelasAI meta tags
  c = c.replace(/<kelasai_meta>[\s\S]*?<\/kelasai_meta>/gi, "");

  // 1b) Remove custom illustration/visual containers (and their content)
  c = c.replace(/<(illustration|visual|figure|schema|diagram|chart|graph|image|timeline|frise)\b[\s\S]*?<\/\1>/gi, "");
  c = c.replace(/<(illustration|visual|figure|schema|diagram|chart|graph|image|timeline|frise)\b[^>]*\/?>/gi, "");

  // 2) Remove ALL illustration markers (photo, diagram, schema, etc.)
  c = c.replace(/\[ILLUSTRATION(?:_[A-Z]+)?:[^\]]*\]/gi, "");
  c = c.replace(/\[(?:SCHEMA|DIAGRAM|FIGURE|IMAGE|VISUAL|CHART|GRAPH|PHOTO|PICTURE|TIMELINE|FRISE)[^\]]*\]/gi, "");
  // Parenthetical illustration mentions: (illustration: ...), (figure 2), etc.
  c = c.replace(/\(\s*(illustration|figure|schéma|schema|diagramme|graphique|image|visuel|photo|frise|timeline)[^)]*\)/gi, "");

  // 3) Remove titles/captions immediately adjacent to a fenced visual block
  c = c.replace(/(^|\n)\s{0,3}#{1,6}[^\n]*\n+(?=```)/g, "$1");
  c = c.replace(/```[a-z]*\n[\s\S]*?```\s*\n+\s{0,3}#{1,6}[^\n]*(?=\n|$)/gi, (m) =>
    m.replace(/\n+\s{0,3}#{1,6}[^\n]*$/, ""),
  );
  c = c.replace(/(^|\n)\s{0,3}(\*\*|__|\*|_)[^\n*_]{1,80}\2\s*\n+(?=```)/g, "$1");
  c = c.replace(/```[\s\S]*?```\s*\n+\s{0,3}(?:\*\*|__|\*|_)[^\n*_]{1,80}(?:\*\*|__|\*|_)(?=\n|$)/g, (m) =>
    m.replace(/\n+\s{0,3}(?:\*\*|__|\*|_)[^\n*_]{1,80}(?:\*\*|__|\*|_)$/, ""),
  );

  // 4) Remove ALL fenced code blocks — illustrations, SVG, JSON, diagrams, mermaid, plots
  c = c.replace(/```[\s\S]*?```/g, "");
  c = c.replace(/```[\s\S]*?```/g, "");
  c = c.replace(/~~~[\s\S]*?~~~/g, "");
  // During streaming, a visual/code block may be incomplete: drop any dangling fenced block too
  c = c.replace(/```(?:plot|geometry|diagram|map|biology|mermaid|timeline|frise)?[\s\S]*$/gi, "");
  c = c.replace(/~~~(?:plot|geometry|diagram|map|biology|mermaid|timeline|frise)?[\s\S]*$/gi, "");

  // 5) Remove inline code
  c = c.replace(/`[^`]+`/g, "");

  // 6) Remove markdown images
  c = c.replace(/!\[[^\]]*\]\([^)]*\)/g, "");

  // 7) Remove SVG/HTML visual tags entirely
  c = c.replace(
    /<(svg|div|span|table|tr|td|th|img|canvas|figure|figcaption|path|circle|rect|line|polyline|polygon|ellipse|g|defs|use|text|tspan|marker|pattern|filter|mask|clippath|lineargradient|radialgradient|stop|symbol|foreignobject)\b[\s\S]*?<\/\1>/gi,
    "",
  );
  c = c.replace(/<(img|br|hr|use|circle|rect|line|path|polyline|polygon|ellipse|stop)\b[^>]*\/?>/gi, "");
  c = c.replace(/<\/?(svg|g|defs|path|circle|rect|line|polyline|polygon|ellipse|text|tspan|marker|pattern|filter|mask|clippath|lineargradient|radialgradient|symbol|foreignobject)\b[^>]*>/gi, "");

  // 8) Remove LaTeX
  c = c.replace(/\$\$[\s\S]*?\$\$/g, "");
  c = c.replace(/\$[^$\n]+\$/g, "");

  // 9) Remove illustration/figure captions and references
  c = c.replace(
    /^\s*(illustration|illustrations|figure|fig\.?|schéma|schema|diagramme|graphique|image|visuel|légende|titre|frise(?:\s+chronologique)?|timeline)\s*\d*\s*[:\-–—].*$/gim,
    "",
  );
  c = c.replace(
    /^.*\b(voir|regarde[zr]?|observe[zr]?|ci[\s-]?(?:dessus|dessous|contre))\s+(l['’]?\s*|la\s+|une\s+)?(illustration|illustrations|figure|schéma|schema|diagramme|graphique|image|visuel|frise(?:\s+chronologique)?|timeline)\b.*$/gim,
    "",
  );
  // Drop any sentence mentioning "ci-dessus/dessous/contre" + illustration/figure/…
  c = c.replace(
    /[^.!?\n]*\bci[\s-]?(?:dessus|dessous|contre)\b[^.!?\n]*\b(illustration|figure|schéma|schema|diagramme|graphique|image|visuel|frise(?:\s+chronologique)?|timeline)\b[^.!?\n]*[.!?]?/gi,
    "",
  );
  c = c.replace(
    /\(\s*(voir|cf\.?)\s+(l['’]?\s*|la\s+|une\s+)?(illustration|figure|schéma|schema|diagramme|graphique|image|visuel|frise(?:\s+chronologique)?|timeline)[^)]*\)/gi,
    "",
  );
  // Drop entire sentences that describe a frise/timeline/diagram visual
  c = c.replace(
    /[^.!?\n]*\b(frise(?:\s+chronologique)?|timeline|diagramme|schéma|schema)\b[^.!?\n]*[.!?]/gi,
    "",
  );

  // 10) Remove raw JSON / config structures iteratively (incl. nested illustration JSON like {"template":"flower",...})
  for (let i = 0; i < 8; i++) {
    const before = c;
    c = c.replace(/\{[^{}]*\}/g, "");
    if (c === before) break;
  }
  c = c.replace(/\[\s*\{[\s\S]*?\}\s*\]/g, "");
  // Catch any remaining JSON-ish blob that mentions template/labels/parts (illustration descriptors)
  c = c.replace(/\{[\s\S]*?"(?:template|labels|parts|nodes|edges|points|shapes)"[\s\S]*?\}/g, "");

  // 11) Remove markdown formatting chars
  // Remove parenthetical asides entirely (content + brackets) so the voice doesn't read them
  for (let i = 0; i < 4; i++) {
    const before = c;
    c = c.replace(/\([^()]*\)/g, "");
    if (c === before) break;
  }
  c = c.replace(/[#*_~>\[\](){}|]/g, "");

  // 11b) Remove math/separator symbols the voice should not read aloud
  // (hyphens, plus, equals, slashes, arrows, bullets, etc.)
  c = c.replace(/[•·●◦▪►→←↔⇒⇐⇔»«]/g, " ");
  c = c.replace(/[=+]/g, " ");
  // Hyphens/dashes: keep inside words (compound words like "sous-marin"),
  // strip when standalone or used as bullets/separators
  c = c.replace(/(^|\s)[-–—]+(\s|$)/g, "$1$2");

  // 12) Remove emojis
  c = c.replace(
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu,
    "",
  );

  // 13) Multiple-choice letters → natural pause
  c = c.replace(/^([A-D])\.\s/gm, "$1, ");

  // 14) Remove URLs
  c = c.replace(/https?:\/\/\S+/gi, "");

  // 15) Drop residual schema/coordinate lines (SVG paths, x/y values)
  c = c.replace(/^\s*[MLHVCSQTAZmlhvcsqtaz0-9.,\-\s]{20,}$/gm, "");
  c = c.replace(/^\s*(?:[xy][12]?\s*=\s*-?\d+\.?\d*\s*){2,}.*$/gim, "");
  // Drop ASCII / unicode diagram lines (boxes, arrows, connectors, flow charts)
  c = c.replace(/^[\s\-_=+|/\\<>~•·●◦▪►→←↔⇒⇐⇔┌┐└┘├┤┬┴┼│─━║═╔╗╚╝╠╣╦╩╬]+$/gm, "");
  c = c.replace(/^.*[┌┐└┘├┤┬┴┼│─━║═╔╗╚╝╠╣╦╩╬].*$/gm, "");

  // 16) Normalize whitespace
  c = c.replace(/\n{2,}/g, ".\n").replace(/\n/g, ". ").replace(/\s{2,}/g, " ");

  return c.trim().slice(0, 4000);
};

// Split text into spoken segments (sentences)
export const splitIntoSegments = (rawText: string): string[] => {
  const cleaned = preprocessForSpeech(rawText);
  if (!cleaned) return [];
  // Split on sentence boundaries, keeping punctuation
  const parts = cleaned.match(/[^.!?…]+[.!?…]+[\s]*/g) || [cleaned];
  // Merge tiny segments
  const merged: string[] = [];
  let buffer = "";
  for (const p of parts) {
    buffer += p;
    if (buffer.trim().length >= 40) {
      merged.push(buffer.trim());
      buffer = "";
    }
  }
  if (buffer.trim().length > 5) merged.push(buffer.trim());
  return merged;
};

// Find which segment contains a given position in the original text
export const findSegmentForText = (
  originalText: string,
  segments: string[],
  segmentIndex: number
): { start: number; end: number } | null => {
  if (!segments[segmentIndex]) return null;
  const target = segments[segmentIndex];
  // Use first few significant words to locate in original
  const words = target.split(/\s+/).filter(w => w.length > 2).slice(0, 3);
  if (words.length === 0) return null;
  
  // Find approximate location by searching for key words
  const searchStr = words.join(".*?");
  const regex = new RegExp(searchStr, "i");
  const match = originalText.match(regex);
  if (!match || match.index === undefined) return null;
  
  return { start: match.index, end: match.index + match[0].length + 20 };
};

export function useVoiceSync(voice: string, language: string) {
  const [syncState, setSyncState] = useState<VoiceSyncState>({
    segments: [],
    currentSegmentIndex: -1,
    isPlaying: false,
    isPaused: false,
    isFinished: false,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const segmentsRef = useRef<string[]>([]);
  const audioBlobsRef = useRef<Map<number, { blob: Blob; text: string }>>(new Map());
  const currentIndexRef = useRef(-1);
  const isPausedRef = useRef(false);
  const isActiveRef = useRef(false);
  // Track segments already sent for prefetch during streaming
  const streamSentCountRef = useRef(0);
  const earlyStartInitiatedRef = useRef(false);

  const fetchTTSBlob = async (text: string, signal?: AbortSignal): Promise<Blob | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return null;
      const resp = await fetch(TTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, voice }),
        signal,
      });
      const ct = resp.headers.get("Content-Type") || "";
      if (!resp.ok || ct.includes("application/json")) return null;
      const blob = await resp.blob();
      return blob.size > 0 ? blob : null;
    } catch {
      return null;
    }
  };

  const playSegment = useCallback(async (index: number) => {
    if (!isActiveRef.current) return;
    const segments = segmentsRef.current;
    if (index >= segments.length) {
      setSyncState(s => ({ ...s, isPlaying: false, isFinished: true, currentSegmentIndex: -1 }));
      isActiveRef.current = false;
      return;
    }

    currentIndexRef.current = index;
    setSyncState(s => ({ ...s, currentSegmentIndex: index, isPlaying: true, isFinished: false }));

    // Prefetch next segment while playing current
    const nextText = segments[index + 1];
    if (nextText) {
      const nextCached = audioBlobsRef.current.get(index + 1);
      if (!nextCached || nextCached.text !== nextText) {
        fetchTTSBlob(nextText, abortRef.current?.signal).then(blob => {
          if (blob) audioBlobsRef.current.set(index + 1, { blob, text: nextText });
        });
      }
    }

    const segmentText = segments[index];
    let cached = audioBlobsRef.current.get(index);
    let blob: Blob | null = null;
    if (cached && cached.text === segmentText) {
      blob = cached.blob;
    }
    if (!blob) {
      blob = await fetchTTSBlob(segmentText, abortRef.current?.signal);
      if (blob) audioBlobsRef.current.set(index, { blob, text: segmentText });
    }

    if (!blob || !isActiveRef.current) {
      // Skip failed segment
      playSegment(index + 1);
      return;
    }

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audioRef.current = audio;

    const onEnd = () => {
      URL.revokeObjectURL(url);
      audioRef.current = null;
      if (isPausedRef.current || !isActiveRef.current) return;
      playSegment(index + 1);
    };

    audio.onended = onEnd;
    audio.onerror = onEnd;
    audio.play().catch(onEnd);
  }, []);

  // Start full playback from a given index
  const startPlayback = useCallback((fullText: string, fromIndex = 0) => {
    stop();
    const segs = splitIntoSegments(fullText);
    if (segs.length === 0) return;

    segmentsRef.current = segs;
    audioBlobsRef.current.clear();
    abortRef.current = new AbortController();
    isActiveRef.current = true;
    isPausedRef.current = false;
    streamSentCountRef.current = 0;

    setSyncState({
      segments: segs,
      currentSegmentIndex: fromIndex,
      isPlaying: true,
      isPaused: false,
      isFinished: false,
    });

    playSegment(fromIndex);
  }, [playSegment]);

  // Streaming mode: progressively build segments and start playing as soon as first is ready
  const startStreaming = useCallback(() => {
    stop();
    segmentsRef.current = [];
    audioBlobsRef.current.clear();
    abortRef.current = new AbortController();
    isActiveRef.current = true;
    isPausedRef.current = false;
    streamSentCountRef.current = 0;
    earlyStartInitiatedRef.current = false;

    // Unlock audio playback while still inside the user-gesture window.
    // Without this, the first audio.play() after the TTS fetch (which may take
    // 300-1500ms) is blocked by the browser autoplay policy on mobile, and the
    // voice never starts even though the response is rendered.
    try {
      const silent = new Audio(
        "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//FJAAJAMAIwIMAhkSCBMhCnFTQQDQaCAUOLg0M0qC2tJEK6IdJDgD1QKQUlYDQ7lFqWlohQNGSrM6+jKW9bRyhxIcEnW3vKavoqu6OdEUFA7QLEYJ7zCDtwTECVCu64gCEXEklQuvR7t+ZuQVOMaG2cMM7vIIEFTfMTbA1mKkSqRoG4Tn8u0vQVgyk6m4t+H58gB0AjKW1Q4Pm2eEqRWB8XO0VWNK"
      );
      silent.volume = 0;
      silent.play().catch(() => {});
    } catch {
      /* ignore */
    }

    setSyncState({
      segments: [],
      currentSegmentIndex: -1,
      isPlaying: true,
      isPaused: false,
      isFinished: false,
    });
  }, []);

  // Called during streaming with accumulated text so far
  const updateStreamText = useCallback((accumulatedText: string) => {
    if (!isActiveRef.current) return;
    const newSegs = splitIntoSegments(accumulatedText);
    segmentsRef.current = newSegs;
    setSyncState(s => ({ ...s, segments: newSegs }));

    // Prefetch new segments that haven't been sent yet
    const sent = streamSentCountRef.current;
    // Always prefetch segment 0 immediately so playback can start ASAP.
    // For subsequent segments, only prefetch up to the second-to-last
    // (the very last may still be incomplete during streaming).
    const safeBound = newSegs.length === 1 ? 1 : Math.max(0, newSegs.length - 1);
    for (let i = sent; i < safeBound; i++) {
      const idx = i;
      const segText = newSegs[idx];
      const cached = audioBlobsRef.current.get(idx);
      if (!cached || cached.text !== segText) {
        fetchTTSBlob(segText, abortRef.current?.signal).then(blob => {
          if (blob) {
            audioBlobsRef.current.set(idx, { blob, text: segText });
            // Auto-start if we're waiting for the first segment
            if (idx === 0 && currentIndexRef.current === -1 && isActiveRef.current && !isPausedRef.current) {
              playSegment(0);
            }
          }
        });
      }
      streamSentCountRef.current = idx + 1;
    }

    // Early-start: if voice hasn't begun and we have segments, play immediately
    if (!earlyStartInitiatedRef.current && currentIndexRef.current === -1 && newSegs.length > 0 && isActiveRef.current && !isPausedRef.current) {
      earlyStartInitiatedRef.current = true;
      playSegment(0);
    }
  }, [playSegment]);

  // Called when streaming is done — flush final segment
  const finishStreaming = useCallback((finalText: string) => {
    if (!isActiveRef.current) return;
    const segs = splitIntoSegments(finalText);
    segmentsRef.current = segs;
    setSyncState(s => ({ ...s, segments: segs }));

    // Prefetch any remaining segments
    for (let i = streamSentCountRef.current; i < segs.length; i++) {
      const idx = i;
      const segText = segs[idx];
      const cached = audioBlobsRef.current.get(idx);
      if (!cached || cached.text !== segText) {
        fetchTTSBlob(segText, abortRef.current?.signal).then(blob => {
          if (blob) {
            audioBlobsRef.current.set(idx, { blob, text: segText });
            if (idx === 0 && currentIndexRef.current === -1 && isActiveRef.current && !isPausedRef.current) {
              playSegment(0);
            }
          }
        });
      }
    }
    streamSentCountRef.current = segs.length;
  }, [playSegment]);

  const pause = useCallback(() => {
    isPausedRef.current = true;
    audioRef.current?.pause();
    setSyncState(s => ({ ...s, isPaused: true }));
  }, []);

  const resume = useCallback(() => {
    isPausedRef.current = false;
    setSyncState(s => ({ ...s, isPaused: false }));
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const stop = useCallback(() => {
    isActiveRef.current = false;
    isPausedRef.current = false;
    abortRef.current?.abort();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();
    currentIndexRef.current = -1;
    streamSentCountRef.current = 0;
    earlyStartInitiatedRef.current = false;
    setSyncState({
      segments: [],
      currentSegmentIndex: -1,
      isPlaying: false,
      isPaused: false,
      isFinished: false,
    });
  }, []);

  const skipForward = useCallback(() => {
    if (!isActiveRef.current) return;
    const next = currentIndexRef.current + 1;
    if (next < segmentsRef.current.length) {
      audioRef.current?.pause();
      audioRef.current = null;
      playSegment(next);
    }
  }, [playSegment]);

  const skipBackward = useCallback(() => {
    if (!isActiveRef.current) return;
    const prev = Math.max(0, currentIndexRef.current - 1);
    audioRef.current?.pause();
    audioRef.current = null;
    playSegment(prev);
  }, [playSegment]);

  const replay = useCallback((fullText: string) => {
    startPlayback(fullText, 0);
  }, [startPlayback]);

  return {
    syncState,
    startPlayback,
    startStreaming,
    updateStreamText,
    finishStreaming,
    pause,
    resume,
    stop,
    skipForward,
    skipBackward,
    replay,
  };
}
